import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Lock, ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Sparks from "@/components/Sparks";
import {
  apiClient,
  getSessionId,
  getProfileKey,
  getLastConsultationId,
} from "@/lib/api";
import { track } from "@/lib/analytics";
import { useLang } from "@/lib/i18n";

const DEFAULT_PAYWALL = {
  title: "You've reached the end of your current clarity journey",
  subtitle: "Continue with a deeper personalized guidance session.",
  benefits: [
    "Personalized clarity guidance",
    "Decision analysis",
    "Action recommendations",
  ],
  packs: [
    {
      id: "pack_1",
      label: "1 additional clarity question",
      questions: 1,
      price_inr: 299,
      recommended: false,
      features: [
        "One additional question",
        "Personalized clarity guidance",
        "Decision analysis",
        "Action recommendations",
      ],
    },
    {
      id: "pack_3",
      label: "3 additional clarity questions",
      questions: 3,
      price_inr: 599,
      recommended: true,
      save_label: "Save ₹298",
      features: [
        "Three additional questions",
        "Deeper clarity analysis",
        "Behavioral insights",
        "Action roadmap",
        "Premium guidance",
      ],
    },
  ],
};

export default function Paywall() {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [content, setContent] = useState(DEFAULT_PAYWALL);
  const [purchasing, setPurchasing] = useState(null);
  const [teaser, setTeaser] = useState(null);

  useEffect(() => {
    try {
      track("paywall_view");
    } catch (_) {}
    try {
      const cached = localStorage.getItem("mantrana_paywall");
      if (cached) {
        const parsed = JSON.parse(cached);
        setContent({ ...DEFAULT_PAYWALL, ...parsed });
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const cid = getLastConsultationId();
    if (!cid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/consultations/${cid}`, {
          skipPaywall: true,
        });
        if (cancelled) return;
        const reading = res.data?.reading || {};
        const firstName = (
          res.data?.birth?.full_name || ""
        )
          .trim()
          .split(/\s+/)[0];
        // Build two teaser cards from real reading content
        const items = [];
        const ninety = reading.action_plan?.ninety_day;
        if (ninety) {
          items.push({
            label: firstName
              ? t("pw.teaser.90day", { name: firstName })
              : t("pw.teaser.90day.you"),
            body: ninety,
          });
        }
        const spiritual =
          reading.spiritual_practice?.meditation ||
          reading.spiritual_practice?.mantra;
        if (spiritual) {
          items.push({
            label: t("pw.teaser.practice"),
            body: spiritual,
          });
        }
        // Fallbacks if reading missing those keys
        if (items.length < 2) {
          if (reading.recommended_action && items.length < 2) {
            items.push({
              label: t("pw.teaser.nextStep"),
              body: reading.recommended_action,
            });
          }
          if (reading.biggest_blind_spot && items.length < 2) {
            items.push({
              label: t("pw.teaser.blindSpot"),
              body: reading.biggest_blind_spot,
            });
          }
        }
        if (items.length >= 2) setTeaser(items.slice(0, 2));
      } catch (_) {
        /* silently skip teaser */
      }
    })();
    return () => {
      cancelled = true;
    };
     
  }, []);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (typeof window !== "undefined" && window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const grantAndRedirect = (remaining) => {
    try {
      localStorage.setItem("mantrana_remaining_questions", String(remaining));
      localStorage.removeItem("mantrana_paywall");
    } catch (_) {}
    toast.success(
      t("purchase.unlocked", { n: remaining, s: remaining === 1 ? "" : "s" })
    );
    navigate("/");
  };

  const purchase = async (packId) => {
    if (purchasing) return;
    setPurchasing(packId);
    try {
      const sid = getSessionId();
      const pk = getProfileKey();
      // Try Razorpay first
      const orderRes = await apiClient
        .post(
          "/payments/create-order",
          { session_id: sid, pack_id: packId, profile_key: pk || null },
          { skipPaywall: true, validateStatus: () => true }
        )
        .catch(() => null);

      if (orderRes && orderRes.status === 200 && orderRes.data?.key_id) {
        const ok = await loadRazorpayScript();
        if (!ok) throw new Error("Razorpay checkout failed to load");
        const data = orderRes.data;
        const RZP = window.Razorpay;
        const rzp = new RZP({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          order_id: data.order_id,
          name: "Mantrana One",
          description: data.pack?.label || "Clarity pack",
          theme: { color: "#D4AF37" },
          handler: async (resp) => {
            try {
              const verify = await apiClient.post(
                "/payments/verify",
                {
                  session_id: sid,
                  pack_id: packId,
                  profile_key: pk || null,
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                },
                { skipPaywall: true }
              );
              grantAndRedirect(verify.data?.remaining_paid_questions ?? 0);
            } catch (err) {
              console.error(err);
              toast.error(t("purchase.verifyFailed"));
              setPurchasing(null);
            }
          },
          modal: {
            ondismiss: () => setPurchasing(null),
          },
        });
        rzp.open();
        return;
      }

      // Fallback: demo checkout (used when Razorpay keys are not configured)
      const res = await apiClient.post(
        "/purchase",
        { session_id: sid, pack_id: packId, profile_key: pk || null },
        { skipPaywall: true }
      );
      grantAndRedirect(res.data?.remaining_paid_questions ?? 0);
    } catch (e) {
      console.error(e);
      toast.error(t("purchase.failed"));
      setPurchasing(null);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg flex flex-col" data-testid="paywall-page">
      <Sparks />
      <header className="relative max-w-7xl w-full mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2"
          data-testid="paywall-home-link"
        >
          <Sparkles className="w-5 h-5 gold-text" strokeWidth={1.5} />
          <span className="font-serif text-xl text-white">
            {lang === "hi" ? (
              <>मंत्रणा<span className="gold-text"> One</span></>
            ) : (
              <>Mantrana<span className="gold-text"> One</span></>
            )}
          </span>
        </button>
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-white transition-colors"
          data-testid="paywall-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t("common.home")}
        </button>
      </header>

      <main className="relative flex-1 px-6 py-8 md:py-16">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="text-center mb-12"
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] tracking-[0.3em] uppercase mb-5"
              style={{
                background: "rgba(212,175,55,0.12)",
                border: "1px solid rgba(212,175,55,0.4)",
                color: "#FBE38E",
                boxShadow: "0 0 16px rgba(212,175,55,0.18)",
              }}
              data-testid="paywall-badge"
            >
              <Lock className="w-3 h-3" />
              {t("pw.premiumRequired")}
            </div>
            <h1
              className="font-serif text-3xl md:text-5xl font-light text-white leading-tight"
              data-testid="paywall-title"
            >
              {t("pw.title")}
            </h1>
            <p
              className="mt-4 text-base md:text-lg text-slate-300 font-light max-w-2xl mx-auto"
              data-testid="paywall-subtitle"
            >
              {t("pw.subtitle")}
            </p>
          </motion.div>

          {/* Locked teaser — shows real snippets from the user's just-finished reading */}
          {teaser && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
              className="mb-10"
              data-testid="paywall-teaser"
            >
              <div className="text-center mb-5">
                <div className="text-[10px] tracking-[0.3em] uppercase gold-text">
                  {t("pw.previous.eyebrow")}
                </div>
                <p className="mt-2 text-sm text-slate-400 font-light">
                  {t("pw.previous.blurred")}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {teaser.map((tItem, i) => (
                  <TeaserCard key={i} label={tItem.label} body={tItem.body} t={t} />
                ))}
              </div>
            </motion.div>
          )}

          {/* Pack cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="paywall-packs">
            {(content.packs || []).map((pack, i) => (
              <PackCard
                key={pack.id}
                pack={pack}
                delay={0.15 + i * 0.1}
                purchasing={purchasing === pack.id}
                onBuy={() => purchase(pack.id)}
                benefits={content.benefits}
                t={t}
              />
            ))}
          </div>

          <div className="mt-10 text-center">
            <button
              onClick={() => navigate("/")}
              className="rounded-full px-7 py-3 text-sm font-medium border border-white/20 hover:bg-white/5 text-white transition-colors"
              data-testid="paywall-return-home"
            >
              {t("common.returnHome")}
            </button>
            <p className="mt-6 text-center text-xs text-slate-500 max-w-md mx-auto">
              {t("pw.footer.note")}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

function PackCard({ pack, delay, purchasing, onBuy, benefits, t }) {
  const isRecommended = pack.recommended;
  const localizedLabel = pack.questions === 1
    ? t("pw.pack.1q")
    : pack.questions === 3
    ? t("pw.pack.3q")
    : pack.label;
  // Localized feature bullets per pack.id — ignore whatever the backend/
  // DEFAULT_PAYWALL declared in English. Falls back to a shared list.
  const featureKeys = pack.id === "pack_1"
    ? ["pw.pack.pack_1.f1", "pw.pack.pack_1.f2", "pw.pack.pack_1.f3", "pw.pack.pack_1.f4"]
    : pack.id === "pack_3"
    ? ["pw.pack.pack_3.f1", "pw.pack.pack_3.f2", "pw.pack.pack_3.f3", "pw.pack.pack_3.f4", "pw.pack.pack_3.f5"]
    : ["pw.pack.default.f1", "pw.pack.default.f2", "pw.pack.default.f3", "pw.pack.default.f4"];
  const localizedFeatures = featureKeys.map((k) => t(k));
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className="relative rounded-3xl p-8 md:p-10 overflow-hidden flex flex-col"
      style={{
        background: isRecommended
          ? "linear-gradient(160deg, rgba(212,175,55,0.18) 0%, rgba(49,46,129,0.30) 60%, rgba(20,17,46,0.92) 100%)"
          : "linear-gradient(160deg, rgba(19,25,43,0.92) 0%, rgba(20,17,46,0.92) 60%, rgba(35,29,80,0.92) 100%)",
        border: isRecommended
          ? "1px solid rgba(212,175,55,0.5)"
          : "1px solid rgba(255,255,255,0.08)",
        boxShadow: isRecommended
          ? "0 30px 80px rgba(0,0,0,0.55), 0 0 80px rgba(212,175,55,0.22), inset 0 1px 0 rgba(255,255,255,0.06)"
          : "0 20px 50px rgba(0,0,0,0.4)",
      }}
      data-testid={`pack-${pack.id}`}
    >
      {isRecommended && (
        <div
          className="absolute top-5 right-5 px-3 py-1 rounded-full text-[9px] tracking-[0.3em] uppercase"
          style={{ background: "#D4AF37", color: "#0B1020" }}
        >
          {t("pricing.recommended")}
        </div>
      )}
      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-serif text-5xl md:text-6xl text-white">₹{pack.price_inr}</span>
        {pack.save_label && (
          <span className="text-xs tracking-[0.2em] uppercase gold-text">
            {pack.save_label}
          </span>
        )}
      </div>
      <div className="font-serif text-xl text-slate-200 mb-8 italic font-light">
        {localizedLabel}
      </div>

      <ul className="space-y-2.5 mb-10 flex-1">
        {localizedFeatures.map((b) => (
          <li
            key={b}
            className="flex items-start gap-2.5 text-sm text-slate-200 font-light"
          >
            <Check
              className="w-4 h-4 mt-0.5 flex-shrink-0"
              style={{ color: "#D4AF37" }}
              strokeWidth={2}
            />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={onBuy}
        disabled={purchasing}
        className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-medium ${
          isRecommended ? "cta-gold" : "border border-white/20 hover:bg-white/5 text-white"
        }`}
        data-testid={`pack-buy-${pack.id}`}
      >
        {purchasing ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> {t("pw.processing")}
          </>
        ) : pack.questions === 1 ? (
          t("pricing.pack.getOne", { price: pack.price_inr })
        ) : (
          t("pricing.pack.getN", { n: pack.questions, price: pack.price_inr })
        )}
      </button>
      <p className="mt-3 text-center text-[10px] tracking-[0.2em] uppercase text-slate-500">
        {t("pw.demo.note")}
      </p>
    </motion.div>
  );
}

function TeaserCard({ label, body, t }) {
  return (
    <div
      className="relative rounded-2xl overflow-hidden p-5 md:p-6"
      style={{
        background:
          "linear-gradient(160deg, rgba(19,25,43,0.85) 0%, rgba(20,17,46,0.85) 60%, rgba(35,29,80,0.85) 100%)",
        border: "1px solid rgba(212,175,55,0.22)",
        boxShadow:
          "0 18px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
      data-testid="paywall-teaser-card"
    >
      {/* Header — visible, hints at the depth */}
      <div className="flex items-center justify-between mb-3 relative z-10">
        <div className="text-[9px] tracking-[0.3em] uppercase gold-text">
          {label}
        </div>
        <div
          className="inline-flex items-center justify-center w-7 h-7 rounded-full"
          style={{
            background: "rgba(212,175,55,0.14)",
            border: "1px solid rgba(212,175,55,0.45)",
          }}
        >
          <Lock className="w-3 h-3" style={{ color: "#FBE38E" }} />
        </div>
      </div>

      {/* Body — blurred preview */}
      <div className="relative">
        <div
          className="font-serif text-[15px] md:text-base text-white/90 leading-snug select-none pointer-events-none"
          style={{
            filter: "blur(6px)",
            WebkitFilter: "blur(6px)",
          }}
          aria-hidden="true"
        >
          {body}
        </div>
        {/* Top fade so the blur edges blend in */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-10 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(11,16,32,0.55))",
          }}
        />
      </div>

      {/* Subtle unlock hint */}
      <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-slate-400 relative z-10">
        <Sparkles className="w-3 h-3 gold-text" strokeWidth={1.5} />
        {t ? t("pw.teaser.unlock") : "Unlock with a clarity pack"}
      </div>
    </div>
  );
}
